import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CryptoJS from 'crypto-js';
import { AppContext } from '../contexts/AppContext';
import '../styles/ThankYou.css';
import config from '../Config'; 
const API_BASE_URL = config.api.baseUrl;

const ENCRYPTION_KEY = '12345'; // 必須同你加密時用嘅 key 一樣（這是加密金鑰，用來解密 URL 參數。如果需要調整，請確保與加密時一致。大小調整不適用於此變量。）

function ThankYou() {
  const params = new URLSearchParams(window.location.search);
  const encryptedData = params.get('encryptedData');

  const navigate = useNavigate();
  const { currentLang, setCurrentLang, carParkInfo } = useContext(AppContext);

  // State（狀態變量）
  const [receiptData, setReceiptData] = useState(null); // 收據數據（用來儲存從 API 取得的付款詳情。如果需要調整大小，不適用於此變量。）
  const [loading, setLoading] = useState(true); // 載入狀態（顯示載入中。如果需要調整大小，不適用於此變量。）
  const [error, setError] = useState(null); // 錯誤狀態（顯示錯誤訊息。如果需要調整大小，不適用於此變量。）

  // ── 語言選單相關 ──（語言選擇功能）
  const [anchorEl, setAnchorEl] = useState(null); // 錨點元素（用來定位語言選單。如果需要調整大小，不適用於此變量。）
  const open = Boolean(anchorEl); // 選單是否開啟（布林值。如果需要調整大小，不適用於此變量。）
  const handleClick = (event) => setAnchorEl(event.currentTarget); // 點擊處理（開啟選單。如果需要調整大小，不適用於此變量。）
  const handleClose = () => setAnchorEl(null); // 關閉處理（關閉選單。如果需要調整大小，不適用於此變量。）
  const handleLangChange = (lang) => {
    setCurrentLang(lang); // 改變語言（設定當前語言。如果需要調整大小，不適用於此變量。）
    handleClose();
  };

  const getCurrentLangLabel = () => {
    const map = { 'zh-tw': '繁中', 'zh-cn': '简中', 'en': 'Eng' }; // 語言標籤對應（顯示當前語言簡稱。如果需要調整大小，不適用於此變量。）
    return map[currentLang] || 'Eng';
  };

  const getOtherLanguages = () => [
    { code: 'zh-tw', label: '繁中' },
    { code: 'zh-cn', label: '简中' },
    { code: 'en', label: 'Eng' }
  ].filter(l => l.code !== currentLang); // 其他語言選項（過濾掉當前語言。如果需要調整大小，不適用於此變量。）

  // ── 多語言翻譯 ──（翻譯字典）
  const translations = {
    'zh-tw': {
      loading: '載入中...',
      errorTitle: '載入失敗',
      errorMsg: '無法載入收據，請稍後再試或聯絡管理員',
      title: '付款成功！',
      receiptHeader: '付款詳情',
      carplateLabel: '車牌號碼',
      entryTimeLabel: '入場時間',
      durationLabel: '停泊時間',
      parkingFeeLabel: '總付款金額',
      paymentMethodLabel: '付款方式',
      referenceNumberLabel: '參考編號',
      exitMessage: '請於 15 分鐘內離場。',
      backToHome: '返回主頁',
      invalidGuid: '無效的 GUID，請重新登入',
      hourSingular: '小時',
      hourPlural: '小時',
      minSingular: '分鐘',
      minPlural: '分鐘'
    },
    'zh-cn': {
      loading: '加载中...',
      errorTitle: '加载失败',
      errorMsg: '无法载入收据，请稍后重试或联络管理员',
      title: '支付成功！',
      receiptHeader: '支付详情',
      carplateLabel: '车牌号码',
      entryTimeLabel: '进场时间',
      durationLabel: '停车时长',
      parkingFeeLabel: '总支付金额',
      paymentMethodLabel: '支付方式',
      referenceNumberLabel: '参考编号',
      exitMessage: '请于 15 分钟内离场。',
      backToHome: '返回主页',
      invalidGuid: '无效的 GUID，请重新登录',
      hourSingular: '小时',
      hourPlural: '小时',
      minSingular: '分钟',
      minPlural: '分钟'
    },
    'en': {
      loading: 'Loading...',
      errorTitle: 'Load Failed',
      errorMsg: 'Failed to load receipt. Please try again later or contact administrator.',
      title: 'Payment Successful!',
      receiptHeader: 'PAYMENT DETAILS',
      carplateLabel: 'Licence Plate Number',
      entryTimeLabel: 'Entry Time',
      durationLabel: 'Parking Duration',
      parkingFeeLabel: 'Total Amount',
      paymentMethodLabel: 'Payment Method',
      referenceNumberLabel: 'Reference Number',
      exitMessage: 'Please exit within 15 minutes.',
      backToHome: 'BACK TO HOME',
      invalidGuid: 'Invalid GUID, please login again',
      hourSingular: 'hr',
      hourPlural: 'hrs',
      minSingular: 'min',
      minPlural: 'mins'
    }
  }; 
  
  // 翻譯內容（根據語言顯示不同文字。如果需要調整大小，不適用於此變量，但您可以修改字串來改變顯示內容。）

  const t = (key) => translations[currentLang]?.[key] || translations['en'][key]; // 取得翻譯（如果當前語言無，則用英文。如果需要調整大小，不適用於此變量。）

  // Mount log + GUID check (original + logs)（頁面載入時檢查）
  useEffect(() => {
    console.log(`[ENQUIRY LOG] PAGE MOUNTED: carParkInfo =`, carParkInfo, `currentLang = ${currentLang}`); // 日誌輸出（頁面載入。如果需要調整大小，不適用於此變量。）
    if (!carParkInfo?.guid) {
      console.log(`[ENQUIRY LOG] STEP 0.1: NO GUID - Setting error & redirect to login`); // 日誌輸出（無 GUID。如果需要調整大小，不適用於此變量。）
      const invalidGuidMsg = t('invalidGuid');
      setError(invalidGuidMsg);
      navigate('/login', { replace: true });
      return;
    }
    console.log(`[ENQUIRY LOG] STEP 0.1: GUID OK = ${carParkInfo.guid}`); // 日誌輸出（GUID 正常。如果需要調整大小，不適用於此變量。）
  }, [carParkInfo?.guid, navigate, currentLang]); // 依賴項（當這些改變時重新執行。如果需要調整大小，不適用於此變量。）

  // ── 語言按鈕 ──（渲染語言按鈕）
  const renderLangButton = () => (
    <div className="icons"> {/* 圖示容器（包含語言圖示和文字。您可以調整 CSS 中的 .icons { gap: 調整間距; font-size: 調整文字大小; } 來改變大小、高度。高度由內容決定，調整 padding 或 margin 可改變位置。 */}
      <IconButton onClick={handleClick} sx={{ color: '#808080', padding: 0 }}> {/* 圖示按鈕（語言圖示。您可以調整 sx={{ fontSize: '調整大小，如 40px' }} 來改變圖示大小。高度由 fontSize 影響。 */}
        <LanguageIcon sx={{ fontSize: '30px' }} /> {/* 語言圖示（大小為 30px。您可以調整 sx={{ fontSize: '大: 40px, 小: 20px' }} 來改變大小。高度與寬度等比例。 */}
      </IconButton>
      <Typography sx={{ ml: 0.5, fontSize: '25px', color: '#808080' }}> {/* 文字（當前語言標籤。您可以調整 sx={{ fontSize: '大: 30px, 小: 20px' }} 來改變文字大小。高度由 fontSize 影響。 */}
        {getCurrentLangLabel()}
      </Typography>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}> {/* 選單（語言選擇。您可以調整 CSS 中的 Menu { width: 調整寬度; } 但 MUI Menu 預設自動調整。高度由內容決定。 */}
        {getOtherLanguages().map(lang => (
          <MenuItem key={lang.code} onClick={() => handleLangChange(lang.code)}> {/* 選單項目（每個語言選項。您可以調整 CSS 中的 MenuItem { fontSize: 調整文字大小; padding: 調整內距來改變高度。 */}
            {lang.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );

  // ── 主要邏輯：解密 → 用 guid + LPN + EntryTime 打 API ──（載入收據數據）
  useEffect(() => {
    async function loadReceipt() {
      try {
        if (!encryptedData) throw new Error('Missing encryptedData in URL'); // 檢查加密數據（如果缺少拋出錯誤。如果需要調整大小，不適用於此變量。）
        const guid = carParkInfo?.guid;
        if (!guid) throw new Error('GUID not found in AppContext'); // 檢查 GUID（如果缺少拋出錯誤。如果需要調整大小，不適用於此變量。）

        // 1. 解密 encryptedData（只攞 LPN 同 EntryTime）（解密 URL 參數。如果需要調整大小，不適用於此變量。）
        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (!decrypted) throw new Error('Decryption failed');

        const parsed = JSON.parse(decrypted);
        const { carplate: lpn, entrytime } = parsed;

        if (!lpn || !entrytime) {
          throw new Error('Missing LPN or EntryTime in decrypted data');
        }

        // 2. 呼叫你嘅後端 API，使用正確的後端 port (從 Swagger: 5269)（呼叫 API 取得數據。如果需要調整大小，不適用於此變量。）
        const apiUrl = API_BASE_URL + '/GetQrPaymentReceipt'; // 修正 port 為後端 Swagger 的 5269

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'guid': guid  // 關鍵：用 AppContext 嘅 guid
          },
          body: JSON.stringify({
            entryTime: entrytime.trim(),
            lpn: lpn.trim().toUpperCase()
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        setReceiptData(result); // 設定收據數據（儲存 API 結果。如果需要調整大小，不適用於此變量。）
        setLoading(false); // 結束載入（設定載入為 false。如果需要調整大小，不適用於此變量。）
      } catch (err) {
        console.error('ThankYou page error:', err); // 錯誤日誌（輸出錯誤。如果需要調整大小，不適用於此變量。）
        setError(err.message); // 設定錯誤（顯示錯誤訊息。如果需要調整大小，不適用於此變量。）
        setLoading(false);
      }
    }

    loadReceipt(); // 執行載入函數（當 encryptedData 或 guid 改變時。如果需要調整大小，不適用於此變量。）
  }, [encryptedData, carParkInfo?.guid]);

  // ── 時間格式化 ── (調整為 screenshot 格式: YYYY-MM-DD HH:MM, 無秒)（格式化日期時間）
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—'; // 如果無日期返回破折號（如果需要調整大小，不適用於此變量，但顯示時文字大小由 CSS 控制。）
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; // 格式化字符串（如果需要調整大小，不適用於此變量。）
  };

  // ── 停留時間格式化 ── (匹配 screenshot: "1 hrs 5 mins")（格式化停留時間）
const formatDuration = (duration) => {
  if (!duration && duration !== 0) return '';

  const durationNum = parseInt(duration, 10);
  if (isNaN(durationNum) || durationNum < 0) return '';

  const hours = Math.floor(durationNum / 60);
  const mins = durationNum % 60;

  if (hours === 0 && mins === 0) return '0 ' + t('minPlural');

  let result = [];
  if (hours > 0) {
    const hoursStr = hours === 1 ? t('hourSingular') : t('hourPlural');
    result.push(`${hours} ${hoursStr}`);
  }
  if (mins > 0) {
    const minsStr = mins === 1 ? t('minSingular') : t('minPlural');
    result.push(`${mins} ${minsStr}`);
  }

  return result.join(' ');
};


  // ── Render ──（渲染頁面）
  if (loading) { // 如果載入中（顯示載入畫面）
    return (
      <Container sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}> {/* 容器（載入畫面。您可以調整 sx={{ minHeight: '調整最小高度，如 80vh (高), 50vh (低)' }} 來改變高度。寬度由 maxWidth 控制，大: 'md', 小: 'xs'。 */}
        <CircularProgress size={60} /> {/* 載入圈（大小為 60。您可以調整 size={大: 80, 小: 40} 來改變大小。高度與寬度等比例。 */}
        <Typography sx={{ mt: 3, fontSize: '1.5rem' }}>{t('loading')}</Typography> {/* 文字（載入中。您可以調整 sx={{ fontSize: '大: 2rem, 小: 1rem' }} 來改變文字大小。高度由 fontSize 影響。 */}
      </Container>
    );
  }

  if (error) { // 如果有錯誤（顯示錯誤畫面）
    return (
      <Container
        maxWidth="sm"
        className="thank-you-container"
        sx={{ position: 'relative' }}
      >
        <header className="header">
          <div className="logo">
            <img src="/wilsonparking.jpg" alt="Wilson Parking Logo" className="logo-icon" />
          </div>
          {renderLangButton()}
        </header>
        <Typography className="thank-you-title" sx={{ color: '#f56565' }}>{t('errorTitle')}</Typography>
        <div className="thank-you-error">
          {t('errorMsg')} ({error})
        </div>
        <Button
          variant="contained"
          size="large"
          fullWidth
          className="home-button"
          onClick={() => navigate('/')}
        >
          {t('backToHome')}
        </Button>
      </Container>
    );
  }

  if (!receiptData) { // 如果無收據數據（返回 null，不顯示）
    return null; // 不會到呢度，但防範（如果需要調整大小，不適用於此變量。）
  }

  const exitMsg = t('exitMessage'); // 離場訊息（取得翻譯。如果需要調整大小，不適用於此變量，但顯示時文字大小由 CSS 控制。）

  return (
    <Container
      maxWidth="sm" // 最大寬度（sm: 中等。您可以調整為 "md" (大) 或 "xs" (小) 來改變寬度。高度由內容決定。
      className="thank-you-container" // CSS 類別（見 CSS 說明。
      sx={{ position: 'relative' }} // 樣式（相對位置。您可以調整 sx={{ padding: '調整內距，如 40px (高), 10px (低)' }} 來改變高度。
    >
      <header className="header"> {/* 標頭（包含 logo 和語言按鈕。您可以調整 CSS 中的 .header { margin-bottom: '調整底部邊距，如 60px (高), 20px (低)', width: '調整寬度' } 來改變大小和高度。 */}
        <div className="logo"> {/* logo 容器（顯示 logo。您可以調整 CSS 中的 .logo { font-size: '調整文字大小' } 但這裡是圖片。 */}
          <img src="/wilsonparking.jpg" alt="Wilson Parking Logo" className="logo-icon" />  {/* 圖片（logo。您可以調整 CSS 中的 .logo-icon { width: '大: 300px, 小: 100px', height: 'auto' (自動調整高度) } 來改變大小。高度由 width 比例決定。 */}
        </div>
        {renderLangButton()} {/* 語言按鈕（見上方說明。 */}
      </header>

      <Typography className="thank-you-title"> {/* 標題（付款成功。您可以調整 CSS 中的 .thank-you-title { font-size: '大: 32px, 小: 24px', margin-bottom: '調整底部邊距，如 40px (高), 20px (低)' } 來改變大小和高度。 */}
        {t('title')}
      </Typography>

      <div className="receipt-card"> {/* 收據卡片（包含詳情。您可以調整 CSS 中的 .receipt-card { width: '大: 100%, 小: 80%', border-radius: '調整圓角，如 30px (大), 10px (小)' } 來改變大小。高度由內容決定。 */}
        <Typography className="receipt-header"> {/* 收據標頭（付款詳情。您可以調整 CSS 中的 .receipt-header { font-size: '大: 20px, 小: 16px', padding: '調整內距，如 20px (高), 10px (低)' } 來改變大小和高度。 */}
          {t('receiptHeader')}
        </Typography>

        <Table className="thank-you-table"> {/* 表格（顯示詳情。您可以調整 CSS 中的 .thank-you-table { padding: '調整內距' } 來改變高度。寬度為 100%。 */}
          <TableBody> {/* 表格主體（行。如果需要調整大小，由 CSS 控制每行。 */}
            <TableRow> {/* 表格行（車牌。您可以調整 CSS 中的 .MuiTableCell-root { padding: '大: 20px, 小: 10px', font-size: '調整文字大小' } 來改變每行高度和大小。 */}
              <TableCell sx={{ width: '50%' }}>{t('carplateLabel')}</TableCell>
              <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                {receiptData.lpn}
              </TableCell>
            </TableRow>
            <TableRow> {/* 表格行（入場時間。見上方說明。 */}
              <TableCell sx={{ width: '50%' }}>{t('entryTimeLabel')}</TableCell>
              <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                {formatDateTime(receiptData.entryTime)}
              </TableCell>
            </TableRow>
            {receiptData.duration && (
              <TableRow> {/* 表格行（停留時間。見上方說明。 */}
                <TableCell sx={{ width: '50%' }}>{t('durationLabel')}</TableCell>
                <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                  {formatDuration(receiptData.duration)}
                </TableCell>
              </TableRow>
            )}
            <TableRow> {/* 表格行（停車費用。見上方說明。 */}
              <TableCell sx={{ width: '50%' }}>{t('parkingFeeLabel')}</TableCell>
              <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                HK${Number(receiptData.parkingFee).toFixed(2)}
              </TableCell>
            </TableRow>
            {receiptData.paymentMethod && (
              <TableRow> {/* 表格行（付款方式。見上方說明。 */}
                <TableCell sx={{ width: '50%' }}>{t('paymentMethodLabel')}</TableCell>
                <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                  {receiptData.paymentMethod}
                </TableCell>
              </TableRow>
            )}
            {receiptData.referenceNumber && (
              <TableRow> {/* 表格行（交易編號。見上方說明。 */}
                <TableCell sx={{ width: '50%' }}>{t('referenceNumberLabel')}</TableCell>
                <TableCell align="right" sx={{ width: '50%', wordBreak: 'break-all' }}>
                  {receiptData.referenceNumber}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Typography className="thank-you-message"> {/* 訊息（離場提醒。您可以調整 CSS 中的 .thank-you-message { font-size: '大: 18px, 小: 14px', margin: '調整邊距，如 40px 0 (高), 20px 0 (低)' } 來改變大小和高度。 */}
        {exitMsg}
      </Typography>

      <Button
        variant="contained" // 樣式（contained: 實心。您可以調整為 "outlined" (邊框) 但不建議。
        size="large" // 大小（large: 大。您可以調整為 "medium" (中) 或 "small" (小) 來改變大小。
        fullWidth // 滿寬（true: 滿寬。您可以設為 false 來縮小寬度。
        className="home-button" // CSS 類別（見 CSS 說明。
        onClick={() => navigate('/')} // 點擊處理（返回主頁。如果需要調整大小，不適用於此變量。）
      >
        {t('backToHome')} {/* 按鈕文字（返回主頁。您可以調整 CSS 中的 .home-button { font-size: '大: 20px, 小: 16px', padding: '調整內距，如 20px (高), 10px (低)' } 來改變大小和高度。 */}
      </Button>
    </Container>
  );
}

export default ThankYou;